import React, { useContext, useEffect, useState } from "react";
import { Box, IconButton, Tooltip, Typography, CircularProgress } from "@mui/material";
import ContentLayout from "../../components/layout-components/ContentLayout";
import { ReactComponent as CloseIconImg } from "../../assets/images/close-icon.svg";
import "./Role.scss";
import TagTextField from "../tags/TagTextField";
import CustomButton from "../../components/CustomButton";
import AppContext from "../../config/AppContext";
import { ReactComponent as EditIcon } from "../../assets/images/edit_icon.svg";
import { ReactComponent as EditIconDark } from "../../assets/images/edit_icon_dark.svg";
import { useTranslation } from "react-i18next";
import { rolesList, updateRole } from "../../services/MemberService";
import { toast } from "sonner";
import Loader from "../../components/Loader";


function Role() {
    const { mode } = useContext(AppContext);
    const { t } = useTranslation();
    const [currentlyEditing, setCurrentlyEditing] = useState(null);
    const [isloading, setIsLoading] = useState(false);
    const [roleStates, setRoleStates] = useState(null);
    const [roleList, setRoleList] = useState([]);

const ROLE_CONFIG = {
    manager: {
        displayName:t("RAL1175"),
        description:t("RAL1177"),
        initialState: {
            isEditing: false,
            saveLoading: false,
            error: "",
            roleName: "Manager",
            roleCode: "MANAGER",
            originalRoleName: "Manager",
            editRoleName: "Manager"
        },
        editable: true
    },
    hrManager: {
        displayName: t("RAL1176"),
        description: t("RAL1178"),
        initialState: {
            isEditing: false,
            saveLoading: false,
            error: "",
            roleName: "HR Manager",
            roleCode: "HR_MANAGER",
            originalRoleName: "HR Manager",
            editRoleName: "HR Manager"
        },
        editable: true
    },
    teamLead: {
        displayName:t("RAL0531"),
        description: t("RAL1179"),
        initialState: {
            isEditing: false,
            saveLoading: false,
            error: "",
            roleName: "Team Lead",
            roleCode: "TEAM LEAD",
            originalRoleName: "Team Lead",
            editRoleName: "Team Lead"
        },
        editable: true
    },

};
    useEffect(() => {
        loadRoleList();
    }, []);

    const loadRoleList = () => {
        setIsLoading(true);
        rolesList()
            .then((response) => {
                if (response.data.code === "UASI0000" && response.data.data) {
                    setRoleList(response.data.data.roles || []);
                    setRoleStates((prev) => {
                        const initialState = {};
                        Object.keys(ROLE_CONFIG).forEach(roleType => {
                            const roleCode = response?.data?.data?.roles?.filter(role => role.code === ROLE_CONFIG[roleType].initialState.roleCode);
                            if (roleCode.length > 0 && roleCode[0]?.code === ROLE_CONFIG[roleType].initialState.roleCode) {
                                const roleob = {
                                    ...ROLE_CONFIG[roleType].initialState,
                                    id: roleCode[0]?.id,
                                    roleName: roleCode[0]?.name,
                                    roleCode: roleCode[0]?.code
                                }
                                initialState[roleType] = roleob;
                            }
                        });
                        return initialState;
                    })
                }
            })
            .catch((error) => {
                console.error("Error loading roles:", error);
            })
            .finally(() => {
                setIsLoading(false);
            });
    }

    const handleEdit = (roleType) => {
        if (!ROLE_CONFIG[roleType]?.editable || currentlyEditing) return;

        setCurrentlyEditing(roleType);
        setRoleStates(prev => ({
            ...prev,
            [roleType]: {
                ...prev[roleType],
                originalRoleName: prev[roleType].roleName,
                editRoleName: prev[roleType].roleName,
                isEditing: true,
                error: ""
            }
        }));
    };

    const handleEditChange = (e, roleType) => {
        if (!ROLE_CONFIG[roleType]?.editable) return;

        const value = e.target.value;
        let error = "";

        if (!value.trim()) {
            error = "Role name cannot be empty";
        } else if (value.length > 25) {
            error = "Role name cannot exceed 25 characters";
        }

        setRoleStates(prev => ({
            ...prev,
            [roleType]: {
                ...prev[roleType],
                editRoleName: value,
                error
            }
        }));
    };

    const isDuplicateRoleName = (roleName, roleType) => {
        if (!roleList || roleList.length === 0) return false;
        const normalizedRoleName = roleName.trim().toLowerCase();
        return roleList.some(role => role.name.trim().toLowerCase() === normalizedRoleName);
    }

    const handleEditSubmit = (roleType, id) => {
        if (!ROLE_CONFIG[roleType]?.editable) return;

        const currentState = roleStates[roleType];

        if (currentState.error || !currentState.editRoleName ||
            currentState.editRoleName === currentState.originalRoleName) return;

        if (isDuplicateRoleName(currentState.editRoleName)) {
            toast.error(t("RAL1167"));
            return
        }
        setRoleStates(prev => ({
            ...prev,
            [roleType]: {
                ...prev[roleType],
                saveLoading: true
            }
        }));

        updateRole(currentState.editRoleName, id)
            .then((res) => {
                if (res.data.code === "UASW0004") {
                    toast.success(t("RAL1165"));
                }
                else {
                    toast.error(res.data.message || t("RAL0689"));
                }
            })
            .catch((error) => {
                console.error("Error updating role:", error);
            })
            .finally(() => {
                setCurrentlyEditing(null);
                loadRoleList();
            });

    };

    const handleCancel = (roleType) => {
        if (!ROLE_CONFIG[roleType]?.editable) return;

        setRoleStates(prev => ({
            ...prev,
            [roleType]: {
                ...prev[roleType],
                isEditing: false,
                error: "",
                editRoleName: prev[roleType].originalRoleName
            }
        }));
        setCurrentlyEditing(null);
    };

    const renderRoleComponent = (roleType) => {
        if (!roleStates) {
            console.error("Role states not loaded yet");
            return <Loader open={!roleStates} />;
        }
        const roleState = roleStates[roleType];
        const roleConfig = ROLE_CONFIG[roleType];

        if (!roleState || !roleConfig) {
            console.error(`Missing configuration for role type: ${roleType}`);
            return null;
        }

        const { isEditing, saveLoading, error, roleName, editRoleName, id } = roleState;
        const { displayName, description, editable } = roleConfig;

        return (
            <div className="role-settings-component" key={roleType}>
                <Box className="role-settings">
                    <span className="role-name">{displayName}</span>
                    <span className={mode === "dark" ? "role-description-dark" : "role-description"}>
                        {description}
                    </span>
                </Box>

                <Box
                    display="flex"
                    alignItems="center"
                    className={"department" + (isEditing && error ? " error-box" : "")}
                    justifyContent="center"
                    width="100%"
                    height="60px"
                    borderRadius="5px"
                    sx={{
                        backgroundColor: isEditing
                            ? "var(--background-color-19)"
                            : "var(--background-color-18)",
                        mt: 1
                    }}
                >
                    <Box width="70%" p={"5px"} m={"5px"} flexGrow={1}>
                        {isEditing ? (
                            <TagTextField
                                id={`textfield-${roleType}-editRoleName`}
                                label={t("RAL1180")}
                                value={editRoleName}
                                onChange={(e) => handleEditChange(e, roleType)}
                                inputProps={{ maxLength: 25 }}
                                error={!!error}
                                helperText={error}
                                fullWidth
                                variant="outlined"
                                size="small"
                            />
                        ) : (
                            <Typography
                                id={`text-${roleType}-roleName`}
                                sx={{
                                    font: "normal normal normal 16px/20px Gotham",
                                    wordBreak: "break-word",
                                    textTransform: "none"
                                }}
                                variant="body1"
                                color="var(--text-secondary)"
                                textAlign="left"
                            >
                                {roleName}
                            </Typography>
                        )}
                    </Box>

                    {editable && (
                        <Box display="flex" flexGrow={1} alignItems="center">
                            {isEditing ? (
                                <>
                                    <Box>
                                        {saveLoading ? (
                                            <Box sx={{ justifyContent: "center", alignItems: "center" }}>
                                                <CircularProgress style={{ width: 25, height: 25 }} />
                                            </Box>
                                        ) : (
                                            <CustomButton
                                                id={`button-${roleType}-submit`}
                                                disabled={error || !editRoleName || editRoleName === roleState.originalRoleName}
                                                size="small"
                                                variant="contained"
                                                onClick={() => handleEditSubmit(roleType, id)}
                                            >
                                                {t("RAL0309")}
                                            </CustomButton>
                                        )}
                                    </Box>
                                    <Box
                                        sx={{
                                            height: "20px",
                                            width: "20px",
                                            background: "#FE5050 0% 0% no-repeat padding-box",
                                            borderRadius: "4px",
                                            opacity: "1",
                                            display: "flex",
                                            justifyContent: "center",
                                            alignItems: "center",
                                            cursor: "pointer",
                                            marginInline: "15px",
                                        }}
                                        onClick={() => handleCancel(roleType)}
                                    >
                                        <Tooltip id={`tooltip-${roleType}-close`} title={t("RAL0177")}>
                                            <CloseIconImg id={`${roleType}-close-icon-img`} style={{ fill: "white" }} />
                                        </Tooltip>
                                    </Box>
                                </>
                            ) : (
                                <Box sx={{ marginLeft: "20px" }}>
                                    <Tooltip id={`tooltip-${roleType}-edit`} title={currentlyEditing ? t("RAL0310") : t("RAL0172")}>
                                        <IconButton
                                            sx={{ borderRadius: "5px" }}
                                            onClick={() => handleEdit(roleType)}
                                            disabled={!!currentlyEditing}
                                        >
                                            {mode === "dark" ? <EditIconDark id={`btn-edit-${roleType}`} /> : <EditIcon id={`btn-edit-${roleType}`} />}
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                            )}
                        </Box>
                    )}
                </Box>
            </div>
        );
    };

    return (
        <ContentLayout
            title={t("RAL0167")}
            type="form"
            navBack={false}
            subtitle={t("RAL1181")}
            isloading={isloading}
        >
            <div>
                {Object.keys(ROLE_CONFIG).map(roleType => renderRoleComponent(roleType))}
            </div>
        </ContentLayout>
    );
}

export default Role;